import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import { AssistantAttachmentTextState } from "ai-assistant/attachments/assistant-attachment-text-state";
import { z } from "zod";

export type AssistantAttachmentJSON = z.infer<typeof AssistantAttachmentSchema.AssistantAttachmentJSONZ>;
export type AssistantAttachmentParams = z.infer<typeof AssistantAttachmentSchema.AssistantAttachmentZ>;

const AssistantAttachmentJSONZ = z.object({
  id: z.uuid(),
  fileName: z.string(),
  mediaType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  // `local` too: a timestamp the server sends without an offset must not cost the whole
  // conversation read its parse.
  uploadedAt: z.iso.datetime({ offset: true, local: true }),
  textState: z.enum(AssistantAttachmentTextState),
});

const AssistantAttachmentZ = z.object({
  id: z.instanceof(Guid),
  fileName: z.string(),
  mediaType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  uploadedAt: z.custom<AHDate>(),
  textState: z.enum(AssistantAttachmentTextState),
});

/** Validated attachment JSON as params. The codec's decode, and `decodeParsed`. */
function decodeAttachment(data: AssistantAttachmentJSON): AssistantAttachmentParams {
  return {
    id: new Guid(data.id),
    fileName: data.fileName,
    mediaType: data.mediaType,
    sizeBytes: data.sizeBytes,
    uploadedAt: new AHDate(data.uploadedAt),
    textState: data.textState as AssistantAttachmentTextState,
  };
}

/**
 * A file the user attached to an assistant message, as the server describes it (#6504).
 *
 * Never its text and never where its bytes are kept: the server sends neither.
 */
export default class AssistantAttachmentSchema {
  public static readonly AssistantAttachmentJSONZ = AssistantAttachmentJSONZ;
  public static readonly AssistantAttachmentZ = AssistantAttachmentZ;

  public static validate(values: unknown): AssistantAttachmentParams {
    return parseOrThrow(AssistantAttachmentZ, values, {
      logError: true,
      schemaName: "AssistantAttachmentSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is AssistantAttachmentParams {
    try {
      AssistantAttachmentSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  /** Validates `json` once and decodes it. */
  public static deserialize(json: AssistantAttachmentJSON): AssistantAttachmentParams {
    if (isNullOrUndefined(json)) return json;
    return parseOrThrow(AssistantAttachmentCodec, json, {
      logError: true,
      schemaName: "AssistantAttachmentSchema",
      operation: "deserialize",
      payloadSample: json,
    });
  }

  /** Decodes JSON a parent schema has already validated, without a second parse. */
  public static decodeParsed(json: AssistantAttachmentJSON): AssistantAttachmentParams {
    return decodeAttachment(json);
  }

  public static serialize(attachment: AssistantAttachmentParams): AssistantAttachmentJSON {
    if (isNullOrUndefined(attachment)) return attachment;
    const parsed = AssistantAttachmentSchema.validate(attachment);
    if (isNullOrUndefined(parsed)) return parsed;
    return AssistantAttachmentCodec.encode(parsed);
  }
}

/** The attachment JSON and its params, as one schema; the upload endpoint's `rawResponseSchema`. */
export const AssistantAttachmentCodec = z.codec(AssistantAttachmentJSONZ, AssistantAttachmentZ, {
  decode: decodeAttachment,
  encode: (attachment) => ({
    id: attachment.id.value,
    fileName: attachment.fileName,
    mediaType: attachment.mediaType,
    sizeBytes: attachment.sizeBytes,
    uploadedAt: attachment.uploadedAt.toISOString(),
    textState: attachment.textState,
  }),
});
